import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  TemplateRef,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { AbilityServiceSignal } from '@casl/angular';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { Store } from '@ngxs/store';
import { AuthorizationModule } from '@wilson/authorization';
import { FeatureFlagsModule } from '@wilson/feature-flags';
import {
  AccurateTimeDetails,
  ActivityWithCategoryAndLocationsAndReportedDetails,
  COMPACT_ACTIVITY_HEIGHT,
  DemarcatorRange,
  Label,
  MIN_SHIFT_WIDTH_PX,
  MINIMAL_ACTIVITY_HEIGHT,
  Project,
  ProjectStatus,
  PublicationStatus,
  RoleAction,
  RolePermissionSubject,
  SHIFT_WIDTH_ADJUSTMENT_FOR_ACTIVITIES_PX,
  ShiftCardBackgroundColor,
  ShiftCategoriesColorCode,
  ShiftCategoriesIdsMap,
  ShiftColorOptions,
  ShiftState,
  TimelineShift,
  UnassignedShift,
} from '@wilson/interfaces';
import {
  ACTION_TOASTER_DURATION,
  NzMessageTimerLineComponent,
} from '@wilson/non-domain-specific/ng-zorro/nz-message';
import { ShiftTimelinePipesModule } from '@wilson/shift-timeline/pipes';
import { PreferredSettingsStateFacadeService } from '@wilson/shift-timeline/services';
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import { NzTooltipBaseDirective, NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { Observable, Subscription } from 'rxjs';
import { DeferredAssignmentShiftCardHelperService } from '../deferred-assignment-shift-card/deferred-assignment-shift-card-helper.service';
import {
  ActivityDragInformation,
  ActivityResizeInformation,
  SelectedItemOutput,
} from '../interface';
import { MessageWithUndoOptionComponent } from '../message-with-undo-option/message-with-undo-option.component';
import { IsShiftSelectedPipe } from '../pipes/is-shift-selected.pipe';
import { ShiftMultiSelectHelperService } from '../services/shift-multi-select-helper.service';
import { ShiftCardTooltipStateService } from '../shift-cards/tooltips/shift-card-tooltip/shift-card-tooltip-state.service';
import { ShiftValidationTooltipWrapperComponent } from '../shift-cards/tooltips/shift-validation-tooltip-wrapper/shift-validation-tooltip-wrapper.component';

@Component({
  selector: 'wilson-assignment-shift-card',
  templateUrl: './assignment-shift-card.component.html',
  styleUrls: ['./assignment-shift-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    NzTooltipModule,
    NzSkeletonModule,
    FeatureFlagsModule,
    FontAwesomeModule,
    ShiftValidationTooltipWrapperComponent,
    IsShiftSelectedPipe,
    MessageWithUndoOptionComponent,
    NzMessageTimerLineComponent,
    ShiftTimelinePipesModule,
    AuthorizationModule,
  ],
})
export class AssignmentShiftCardComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  readonly cdRef = inject(ChangeDetectorRef);
  readonly store = inject(Store);
  readonly shiftCardTooltipStateService = inject(ShiftCardTooltipStateService);
  readonly shiftMultiSelectHelperService = inject(
    ShiftMultiSelectHelperService,
  );
  readonly deferredAssignmentShiftCardHelperService = inject(
    DeferredAssignmentShiftCardHelperService,
  );
  private readonly preferredSettingsStateFacadeService = inject(
    PreferredSettingsStateFacadeService,
  );

  readonly SHIFT_WIDTH_ADJUSTMENT_FOR_ACTIVITIES_PX =
    SHIFT_WIDTH_ADJUSTMENT_FOR_ACTIVITIES_PX;
  static readonly WIDTH_BREAKPOINT_REM = 4;
  static readonly WIDTH_BREAKPOINT_PX = (): number =>
    AssignmentShiftCardComponent.WIDTH_BREAKPOINT_REM *
    parseFloat(getComputedStyle(document.documentElement).fontSize);

  @ViewChild('tooltip') tooltip!: NzTooltipBaseDirective;
  @ViewChildren('shiftPreviewCard') shiftPreviewCardRef!: QueryList<
    ElementRef<HTMLElement>
  >;

  @Input() shift!: Omit<TimelineShift, 'isHighlighted'> | UnassignedShift;
  @Input() shiftId!: string;
  @Input() shiftCategoryId!: string;
  @Input() shiftName!: string;
  @Input() shiftState!: ShiftState;
  @Input() publicationStatus!: PublicationStatus;
  @Input() isShiftCompleted!: boolean;
  @Input() isShiftStarted!: boolean;
  @Input({ required: true }) calculatedShiftWidth!: number | null;
  @Input() numberOfInnerRows = 1;
  @Input() innerRowIndex = 0;

  @Input() showShiftValidation = true;
  @Input() isFiltered!: boolean;
  @Input() isHighlighted!: boolean;
  @Input() isShiftDraggable = false;
  @Input() isMinimalHeight = false;
  @Input() isDragging!: boolean;
  @Input() isMoveMode: boolean | null = false;
  @Input() isShiftVisible: boolean | null = false;
  @Input() isTooltipVisible: boolean | null = false;
  @Input() leftPixelPosition: number | null = null;
  @Input() rightPixelPosition: number | null = null;
  @Input() shiftStartDetail!: AccurateTimeDetails;
  @Input() shiftEndDetail!: AccurateTimeDetails;
  @Input() plannedShiftStartDateTime!: string;
  @Input() plannedShiftEndDateTime!: string;
  @Input() plannedStartLocation!: {
    locationCode: string;
    name: string;
  } | null;
  @Input() plannedEndLocation!: {
    locationCode: string;
    name: string;
  } | null;
  @Input() reportedStartLocation!: {
    locationCode: string;
    name: string;
  } | null;
  @Input() reportedEndLocation!: {
    locationCode: string;
    name: string;
  } | null;
  @Input() frameLength!: number | null;
  @Input() timeframe!: Interval | null;
  @Input() isActivityDraggable = false;
  @Input() showActivities = false;
  @Input()
  rowsActivities: ActivityWithCategoryAndLocationsAndReportedDetails[][] = [];
  @Input() shiftHeight!: number;
  @Input() isSelectionEnabled = false;

  @Input() project!: Project | null;
  @Input() label!: Label | null;
  @Input() loadIconsImmediately = false;

  @Output() previewRef = new EventEmitter<HTMLElement>();
  @Output() selectActivity = new EventEmitter<SelectedItemOutput>();

  @Output() startActivityDrag = new EventEmitter<ActivityDragInformation>();
  @Output() dragging = new EventEmitter<DemarcatorRange[]>();
  @Output() endActivityDrag = new EventEmitter();

  @Output() fillActivityGapLeft = new EventEmitter<ActivityResizeInformation>();
  @Output() handleResizeLeft = new EventEmitter<ActivityResizeInformation>();
  @Output() setSnappingForResize =
    new EventEmitter<ActivityResizeInformation>();
  @Output() fillActivityGapRight =
    new EventEmitter<ActivityResizeInformation>();
  @Output() handleResizeRight = new EventEmitter<ActivityResizeInformation>();

  MIN_WIDTH_PX = MIN_SHIFT_WIDTH_PX;
  MINIMAL_ACTIVITY_HEIGHT = MINIMAL_ACTIVITY_HEIGHT;
  COMPACT_ACTIVITY_HEIGHT = COMPACT_ACTIVITY_HEIGHT;

  public shiftCategoriesColorCode = ShiftCategoriesColorCode;
  public shiftCardBackgroundColor = ShiftCardBackgroundColor;
  public ShiftCategoriesIdsMap = ShiftCategoriesIdsMap;
  public readonly MESSAGE_DURATION = ACTION_TOASTER_DURATION;
  public shiftColorOption$!: Observable<ShiftColorOptions>;
  protected can = inject(AbilityServiceSignal).can;

  isCardVisible = true;
  shiftDuration!: number;
  private subscriptions = new Subscription();
  RoleAction = RoleAction;
  RolePermissionSubject = RolePermissionSubject;

  protected ProjectStatus = ProjectStatus;
  protected ShiftColorOptions = ShiftColorOptions;

  ngOnInit(): void {
    this.shiftColorOption$ =
      this.preferredSettingsStateFacadeService.shiftColorOptions$;
  }

  ngAfterViewInit(): void {
    this.subscriptions.add(
      this.shiftPreviewCardRef.changes.subscribe(() => {
        if (this.shiftPreviewCardRef.get(0)) {
          this.previewRef.emit(this.shiftPreviewCardRef.get(0)?.nativeElement);
        }
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  async checkShiftKeyOnClick(
    event: MouseEvent,
    leftPixelPosition: number | null,
    rightPixelPosition: number | null,
    undoTemplate: TemplateRef<void>,
  ): Promise<void> {
    await this.shiftMultiSelectHelperService.handleClick({
      shiftId: this.shiftId,
      event,
      leftPixelPosition,
      rightPixelPosition,
      singleShiftSelectedCallback: () => {
        // This function is intentionally left empty
      },
      templateRef: undoTemplate,
      messageDuration: this.MESSAGE_DURATION,
      isUnAssignedShift: !this.shift.userId,
    });
  }

  selectActivityCallback(activity: SelectedItemOutput): void {
    this.selectActivity.emit(activity);
  }

  startActivityDragCallback(activity: ActivityDragInformation): void {
    this.startActivityDrag.emit(activity);
  }

  draggingCallback(activity: DemarcatorRange[]): void {
    this.dragging.emit(activity);
  }

  endActivityDragCallback(): void {
    this.endActivityDrag.emit();
  }

  setSnappingForResizeCallback(activity: ActivityResizeInformation): void {
    this.setSnappingForResize.emit(activity);
  }

  fillActivityGapLeftCallback(activity: ActivityResizeInformation): void {
    this.fillActivityGapLeft.emit(activity);
  }

  handleResizeLeftCallback(activity: ActivityResizeInformation): void {
    this.handleResizeLeft.emit(activity);
  }

  fillActivityGapRightCallback(activity: ActivityResizeInformation): void {
    this.fillActivityGapRight.emit(activity);
  }

  handleResizeRightCallback(activity: ActivityResizeInformation): void {
    this.handleResizeRight.emit(activity);
  }
}
